import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Functional & Technical',
    items: [
      { name: 'Odoo Functional Consultant', icon: '☁️' },
      { name: 'Odoo Python Developer', icon: '🐍' },
      { name: 'Odoo Implementation Architect', icon: '🏗️' },
      { name: 'Odoo Integration Developer', icon: '🔌' },
    ]
  },
  {
    title: 'Core Modules',
    items: [
      { name: 'Odoo Sales / CRM', icon: '📈' },
      { name: 'Odoo Inventory & Warehouse', icon: '📦' },
      { name: 'Odoo Manufacturing (MRP)', icon: '🏭' },
      { name: 'Odoo Accounting & Finance', icon: '💰' },
    ]
  },
  {
    title: 'Services & Management',
    items: [
      { name: 'Odoo HR & Payroll', icon: '👥' },
      { name: 'Odoo Website / eCommerce', icon: '🌐' },
      { name: 'Odoo Project Management', icon: '📋' },
      { name: 'Odoo POS (Point of Sale)', icon: '🛒' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Genuine Odoo Platform Expertise',
    text: "Our recruiters understand Odoo's modular architecture in depth — Community vs. Enterprise editions, ORM framework, QWeb templating, and the interdependencies between Sales, Inventory, Accounting, and Manufacturing modules. We assess real implementation skill, not surface-level familiarity.",
  },
  {
    icon: '✔',
    title: 'Rigorous Technical & Functional Vetting',
    text: 'All Odoo consultants are verified for: hands-on implementation experience across Odoo modules, Python/ORM and XML/QWeb development proficiency, third-party integration experience (payment gateways, e-commerce, accounting tools), and industry-specific configuration knowledge.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or full-time. From a single Odoo developer for a custom module build to a full implementation team for a multi-module ERP rollout — we scale to your project scope and budget.',
  },
  {
    icon: '🛡',
    title: 'Upgrade & Migration Specialists',
    text: 'We source consultants experienced in Odoo version upgrades and Community-to-Enterprise migrations — protecting your customizations and data integrity through every transition.',
  },
];

const problemItems = [
  'Misconfigured core modules from consultants unfamiliar with Odoo\'s interconnected Sales, Inventory, and Accounting workflows.',
  'Broken custom modules due to developers lacking real Odoo Python, ORM, and XML/QWeb experience.',
  'Failed third-party integrations from inexperienced developers attempting Shopify, Stripe, Tally, or IoT connections.',
  'Costly rework and technical debt when customizations aren\'t built to survive version upgrades.',
  'Underutilized ERP investment when businesses can\'t unlock Odoo\'s full modular potential due to talent gaps.',
  'Delayed go-lives from data migration errors and incomplete testing by under-qualified resources.',
];

const solutionPoints = [
  'Specialized Odoo recruiters who understand your edition and modules',
  'Verified real Odoo implementation experience — not just platform familiarity',
  'Certified Odoo talent matched to your edition, modules, and industry vertical',
];

const outcomes = [
  {
    title: 'On-time, on-budget Odoo go-lives',
    text: 'Certified consultants who configure modules correctly and avoid costly rework cycles.',
  },
  {
    title: 'Reliable custom development',
    text: 'Python and Odoo-native developers who build customizations that survive future upgrades.',
  },
  {
    title: 'Smooth third-party integrations',
    text: 'Specialists who connect Odoo cleanly to your payment, e-commerce, and accounting tools.',
  },
  {
    title: 'Maximum ERP ROI',
    text: 'Experts who configure Odoo\'s full modular potential to match your actual business workflows.',
  },
  {
    title: 'Reduced technical debt',
    text: 'Vetted developers who follow Odoo best practices and write maintainable, upgrade-safe code.',
  },
  {
    title: 'Scalable Odoo talent pipeline',
    text: 'A ready bench of pre-vetted Odoo professionals aligned to your growth and module expansion roadmap.',
  },
];

const OdooStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Odoo Staffing India | Certified Odoo Developers & Consultants | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides expert Odoo staffing solutions. Hire certified Odoo developers, functional consultants, and implementation specialists for Odoo Community and Enterprise editions."
        />
        <meta
          name="keywords"
          content="Odoo staffing India, Odoo developers India, Odoo consultants India, Odoo implementation India, Odoo customization India, Odoo Community staffing, Odoo Enterprise staffing, Odoo Python developers, Odoo functional experts, ERP staffing India, Odoo migration specialists, Odoo e-commerce developers, Odoo manufacturing experts, Kapil Technologies Odoo"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Odoo Staffing India | Certified Odoo Developers & Consultants | Kapil Technologies"
        />
        <meta
          property="og:description"
          content="Hire pre-vetted Odoo professionals for implementation, customization, and migration. Community & Enterprise specialists available."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Odoo Staffing India | Certified Odoo Developers & Consultants"
        />
        <meta
          name="twitter:description"
          content="Expert Odoo staffing solutions. Hire pre-vetted Odoo developers and consultants for your ERP projects."
        />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/odoo"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781758698/odoo_staffing_services_page_rlh9u3.mp4"
        titleWhite="Certified Odoo ERP Consultants."
        titleGradient="Community & Enterprise Specialists."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Struggling to Find Qualified Odoo Developers and Consultants?"
        description="Kapil Tech connects growing businesses with pre-vetted Odoo professionals — from functional consultants and Python/XML developers to implementation specialists across Sales, Inventory, Manufacturing, Accounting, and Website/eCommerce modules. Whether you run Odoo Community or Enterprise, we deliver talent that gets your ERP right the first time."
        stats={[
          { value: "200+", label: "Odoo Experts Placed" },
          { value: "96%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/odoo1.jpg"
        imageAlt="Odoo Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="Odoo Modules & Roles We Staff"
        description="Our Odoo talent network covers every major functional and technical specialization across the platform:"
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Organizations Choose Us */}
      <TechStaffingServices 
        title="Why Growing Businesses Choose Kapil Tech for Odoo Staffing"
        description="We combine deep Odoo domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is a Shortage of Skilled Odoo Talent Holding Back Your ERP Rollout?"
        description="Odoo's open-source, modular architecture makes it one of the most flexible ERP platforms available — but that same flexibility means implementation quality depends entirely on the expertise of the consultants and developers behind it. Generalist staffing agencies routinely confuse basic Odoo familiarity with genuine implementation experience, leading to misconfigured modules, broken customizations, and ERP rollouts that never deliver their promised ROI."
        problemTitle="The Real Cost of a Wrong Odoo Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Odoo Staffing Partner"
        solutionDescription="Kapil Tech maintains a focused Odoo staffing practice with recruiters who understand the real difference between an Odoo functional consultant, a Python/Odoo developer, and a certified Odoo implementation partner resource. Whether you need a functional consultant to configure Sales and CRM, a Python developer for custom modules, an eCommerce specialist, or a full implementation team — we match certified talent to your needs."
        solutionPoints={solutionPoints}
        ctaText="Find Your Odoo Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to achieve measurable business results and maximize your Odoo investment through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Odoo Experts?"
        description="Share your Odoo staffing requirements today. Our team responds within hours with pre-vetted Odoo consultants and developers matched to your edition, modules, and business goals."
      />
    </div>
  );
};

export default OdooStaffHome;
